// src/components/InvoiceValidationInterface.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Download, Target } from 'lucide-react';

import Sidebar from './shared/Sidebar';

import InvoicesTab from './tabs/InvoicesTab';
import TransactionsTab from './tabs/TransactionsTab';
import MatchingTab from './tabs/MatchingTab';
import AnalyticsTab from './tabs/AnalyticsTab';

const InvoiceValidationInterface = () => {
  const [invoices, setInvoices] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const [editMode, setEditMode] = useState(false);
  const [matchMode, setMatchMode] = useState(false);

  const [filters, setFilters] = useState({
    status: '',
    supplier: '',
    dateFrom: '',
    dateTo: '',
  });

  const [activeTab, setActiveTab] = useState('invoices');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // MOCK DATA
  useEffect(() => {
    const mockInvoices = [
      {
        id: '2025-01-01_Twilio Ireland Limited_TWL-2025-001',
        invoice_date: '2025-01-01',
        supplier_name: 'Twilio Ireland Limited',
        invoice_number: 'TWL-2025-001',
        total_amount: 18.45,
        currency: 'USD',
        tax_amount: 0,
        description: 'SMS Services',
        status: 'pending_validation',
        matched_transaction_id: null,
        matching_confidence: null,
      },
      {
        id: '2025-02-28_Microsoft Ireland_MS-2025-228',
        invoice_date: '2025-02-28',
        supplier_name: 'Microsoft Ireland Operations Limited',
        invoice_number: 'MS-2025-228',
        total_amount: 13.2,
        currency: 'EUR',
        tax_amount: 2.64,
        description: 'Office 365 Subscription',
        status: 'validated',
        matched_transaction_id: 'TRANS_2025-03-01_MILLENNIUM_BCP_MS_MATCH',
        matching_confidence: 92,
      },
    ];

    const mockTransactions = [
      {
        id: 'TRANS_2025-03-01_MILLENNIUM_BCP_MS_MATCH',
        booking_date: '2025-03-01',
        transaction_amount: -13.2,
        currency: 'EUR',
        remittance_information: 'DD MICROSOFT IRELAND OPERATIONS LIMITED',
        matched_invoice_id: '2025-02-28_Microsoft Ireland_MS-2025-228',
        matching_status: 'matched',
        matching_confidence: 92,
        transaction_type: 'debit',
      },
    ];

    setInvoices(mockInvoices);
    setTransactions(mockTransactions);
  }, []);

  // helpers
  const formatCurrency = (amount, currency) =>
    new Intl.NumberFormat('pt-PT', {
      style: 'currency',
      currency: currency || 'EUR',
    }).format(amount || 0);

  const hasIssues = (inv) =>
    !inv?.supplier_name ||
    inv.supplier_name === 'UNKNOWN' ||
    !inv?.invoice_date ||
    (inv.total_amount ?? 0) === 0;

  // filtros e estatísticas
  const filteredInvoices = useMemo(() => {
    return invoices.filter((invoice) => {
      if (filters.status && invoice.status !== filters.status) return false;
      if (
        filters.supplier &&
        !(invoice.supplier_name || '')
          .toLowerCase()
          .includes(filters.supplier.toLowerCase())
      )
        return false;
      if (filters.dateFrom && invoice.invoice_date < filters.dateFrom) return false;
      if (filters.dateTo && invoice.invoice_date > filters.dateTo) return false;
      return true;
    });
  }, [invoices, filters]);

  const stats = useMemo(() => {
    const base = {
      pending: 0,
      validated: 0,
      exported: 0,
      totalAmount: 0,
      matched: 0,
      unmatched: 0,
    };
    const s = invoices.reduce((acc, inv) => {
      acc[
        inv.status === 'pending_validation'
          ? 'pending'
          : inv.status === 'validated'
          ? 'validated'
          : 'exported'
      ]++;
      acc.totalAmount += inv.total_amount || 0;
      inv.matched_transaction_id ? acc.matched++ : acc.unmatched++;
      return acc;
    }, base);
    return {
      ...s,
      matchRate: invoices.length
        ? Math.round((s.matched / invoices.length) * 100)
        : 0,
    };
  }, [invoices]);

  // ações
  const handleStartMatching = (invoice) => {
    setSelectedInvoice(invoice);
    setMatchMode(true);
  };

  const handleEdit = (invoice) => {
    setSelectedInvoice(invoice);
    setEditMode(true);
  };

  const openInvoicePdf = async (invoice) => {
    window.open(
      `https://s3.amazonaws.com/fake-bucket/${invoice.id}.pdf`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const handleExportToSheets = async () => {
    alert('Exportando faturas validadas para Google Sheets...');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <div
        className={`${
          sidebarCollapsed ? 'w-20' : 'w-64'
        } bg-white border-r border-gray-200 transition-all duration-300 flex flex-col`}
      >
        <Sidebar
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed((v) => !v)}
          activeTab={activeTab}
          onChangeTab={setActiveTab}
        />
      </div>

      {/* Main */}
      <div className="flex-1 overflow-auto">
        {activeTab === 'invoices' && (
          <InvoicesTab
            invoices={filteredInvoices}
            rawInvoices={invoices}
            filters={filters}
            setFilters={setFilters}
            formatCurrency={formatCurrency}
            hasIssues={hasIssues}
            onStartMatching={handleStartMatching}
            onEdit={handleEdit}
            onOpenPdf={openInvoicePdf}
          />
        )}

        {activeTab === 'transactions' && (
          <TransactionsTab
            transactions={transactions}
            formatCurrency={formatCurrency}
          />
        )}

        {activeTab === 'matching' && (
          <MatchingTab
            invoices={invoices}
            transactions={transactions}
            stats={stats}
            formatCurrency={formatCurrency}
            onStartMatching={handleStartMatching}
          />
        )}

        {activeTab === 'analytics' && <AnalyticsTab stats={stats} />}
      </div>
    </div>
  );
};

export default InvoiceValidationInterface;
